import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import Logo from "Assets/Common/logo.png";

import UseServices from "Hooks/UseServices";

import Button from "Components/Global/Buttons";
import Loading from "Components/Global/Loading";

import * as S from "./styles";

/**
 * Página PÚBLICA da resposta do proponente ao e-mail de validação.
 *
 * O link do e-mail traz um único parâmetro opaco (`?t=<token assinado>`) que
 * carrega o vínculo e a resposta. Aqui a resposta é registrada em um clique,
 * **sem login**, e o resultado aparece em um modal centralizado — o toast no
 * canto da tela passava desapercebido.
 */
const ProjectValidation = () => {
  const { api } = UseServices();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = searchParams.get("t");

  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);

  // O React 18 em dev monta duas vezes; sem o ref a resposta seria enviada em
  // duplicidade (o backend é idempotente, mas evitamos a chamada redundante).
  const requested = useRef(false);

  useEffect(() => {
    if (requested.current) return;

    requested.current = true;

    if (!token) {
      setLoading(false);
      setResult({
        variant: "error",
        title: "Link inválido",
        message:
          "Este endereço está incompleto. Abra o link diretamente do e-mail que você recebeu.",
      });

      return;
    }

    const validate = async () => {
      try {
        const { data } = await api.post("/projects/validate-project-by-token", {
          token,
        });

        setResult({
          variant: data?.authorized ? "success" : "info",
          title: data?.authorized
            ? "Avaliação autorizada!"
            : "Avaliação não autorizada",
          message: data?.message,
          project_name: data?.project_name,
        });
      } catch (error) {
        // A mensagem do backend já é amigável (link expirado, inválido,
        // solicitação removida, resposta já registrada) — não usamos o
        // HTTPErrorCallback para não cair no toast discreto: aqui o retorno é o
        // próprio conteúdo da tela.
        //
        // ⚠️ O erro vem ENVELOPADO: `{ statusCode, body: { error } }`. Ler só
        // `data.error` (como era até 20/08/2026) caía sempre no texto genérico —
        // nenhuma mensagem do backend chegava à tela. Mesma ordem de fallback do
        // HTTPErrorCallback em Helpers/Error.js.
        setResult({
          variant: "error",
          title: "Não foi possível registrar sua resposta",
          message:
            error?.response?.data?.body?.error ||
            error?.response?.data?.error ||
            "Tente novamente pelo link do e-mail ou acesse a plataforma para responder.",
        });
      } finally {
        setLoading(false);
      }
    };

    validate();
    // eslint-disable-next-line
  }, [token]);

  return (
    <S.Wrapper>
      <S.Modal
        role="dialog"
        aria-modal="true"
        aria-labelledby="validation-title"
      >
        <S.Logo src={Logo} alt="Sevenx" />

        {loading ? (
          <>
            <Loading />
            <S.Message>Registrando sua resposta...</S.Message>
          </>
        ) : (
          <>
            <S.Icon $variant={result?.variant}>
              {result?.variant === "error" ? "!" : "✓"}
            </S.Icon>

            <S.Title id="validation-title">{result?.title}</S.Title>

            <S.Message>
              {result?.message}
              {result?.project_name ? (
                <>
                  <br />
                  Projeto: <S.ProjectName>{result.project_name}</S.ProjectName>
                </>
              ) : null}
            </S.Message>

            <S.Actions>
              <Button
                type="button"
                text="Acessar a plataforma"
                onClick={() => navigate("/home")}
              />
            </S.Actions>
          </>
        )}
      </S.Modal>
    </S.Wrapper>
  );
};

export default ProjectValidation;
